import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client'
import {
  TERM_DEFAULTS,
  normalizeLocale,
  resolveTerms,
  setTerminologyVariables,
  toInterpolationVariables,
} from '../i18n/terminology'
import { useUserContext } from './UserContextProvider'
import type { ResolvedTerm, TermKey, TermOverride, TerminologyResponse } from '../types'

interface TerminologyContextValue {
  /** Every concept, resolved for the current UI language. */
  terms: Record<TermKey, ResolvedTerm>
  /** What the tenant actually typed, unresolved — the editor needs this. */
  overrides: TermOverride[]
  isLoading: boolean
}

const TerminologyContext = createContext<TerminologyContextValue | null>(null)

/**
 * Makes the tenant's vocabulary the vocabulary of the whole app.
 *
 * Two jobs, and the order matters. It points i18next's interpolation defaults
 * at the resolved terms *during render*, so every `t()` beneath it already
 * interpolates the customer's words on the same paint — no flash of our word
 * followed by theirs. And it publishes the resolved terms on a context, for the
 * handful of places that need a bare noun ("Division") rather than a sentence.
 *
 * Sits inside UserContextProvider because it needs the active organization, and
 * above everything that renders text.
 */
export function TerminologyProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const { primaryContext, adminOrganizationId } = useUserContext()
  // An admin-track user has no work membership and therefore no primaryContext,
  // so reading only that left the whole app on built-in words for exactly the
  // people who configure the vocabulary. Mirrors what TerminologyPage does.
  const organizationId = primaryContext?.organizationId ?? adminOrganizationId
  const locale = normalizeLocale(i18n?.language)

  const { data, isLoading } = useQuery<TerminologyResponse>({
    queryKey: ['terminology', organizationId],
    queryFn: () => api.terminology.get(organizationId!).then((res) => res.data),
    enabled: !!organizationId,
    // Terminology changes about as often as a company renames a department.
    staleTime: 30 * 60 * 1000,
  })

  const value = useMemo<TerminologyContextValue>(() => {
    const overrides = data?.overrides ?? []
    // Resolved client-side rather than taken from `data.resolved`: the response
    // is cached per organization, not per language, so switching the UI to
    // Norwegian must not need a refetch to get Norwegian terms.
    const terms = overrides.length > 0 ? resolveTerms(overrides, locale) : TERM_DEFAULTS[locale]

    setTerminologyVariables(i18n, toInterpolationVariables(terms, locale))

    return { terms, overrides, isLoading }
  }, [data?.overrides, i18n, locale, isLoading])

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>
}

/**
 * The tenant's word for a concept.
 *
 * Most UI never needs this — a translated sentence carries its own
 * `{{placeholder}}` and resolves without help. Reach for it when you need a
 * standalone noun: a column header, a filter chip, a select option.
 */
export function useTerminology() {
  const context = useContext(TerminologyContext)
  const { i18n } = useTranslation()

  // Outside a provider (tests, isolated stories) the built-in words are still
  // the right answer, so this degrades to defaults rather than throwing. The
  // optional chain matters: useTranslation can hand back an instance that has
  // not finished initialising, and a missing language is not a reason to take
  // a screen down.
  const terms = context?.terms ?? TERM_DEFAULTS[normalizeLocale(i18n?.language)]

  return useMemo(
    () => ({
      terms,
      overrides: context?.overrides ?? [],
      isLoading: context?.isLoading ?? false,
      /**
       * `definite` matters only in Norwegian, where "the value stream" is a
       * suffix on the noun rather than a separate word.
       */
      term: (key: TermKey, plural = false, definite = false) => {
        const t = terms[key]
        if (definite) return plural ? t.definitePlural : t.definiteSingular
        return plural ? t.plural : t.singular
      },
    }),
    [context?.isLoading, context?.overrides, terms],
  )
}
